import { DayShift } from '@/types';
import { set, startOfMonth, addDays, startOfWeek, eachDayOfInterval } from 'date-fns';

// Use current month for demo, starting from Monday of the current week
const now = new Date();
const currentMonthStart = startOfMonth(now);
// Find the Monday of the 2nd week of the month for a nice demo range
const demoWeekStart = startOfWeek(addDays(currentMonthStart, 7), { weekStartsOn: 1 });

/** 7 days of the demo week (Mon-Sun) for limiting the calendar view */
export const DEMO_WEEK_DAYS: Date[] = eachDayOfInterval({
  start: demoWeekStart,
  end: addDays(demoWeekStart, 6),
});

function dayDate(dayIndex: number): Date {
  return addDays(demoWeekStart, dayIndex);
}

function shiftTime(dayIndex: number, hour: number, minute = 0): Date {
  return set(dayDate(dayIndex), { hours: hour, minutes: minute, seconds: 0, milliseconds: 0 });
}

/** Demo month for the calendar display */
export const DEMO_MONTH = currentMonthStart;

/** Demo employees */
export const DEMO_EMPLOYEES = [
  { id: 'demo-emp-1', name: 'Peter K.', email: 'peter@demo.com', avatar_url: null },
  { id: 'demo-emp-2', name: 'Lisa S.', email: 'lisa@demo.com', avatar_url: null },
  { id: 'demo-emp-3', name: 'Tomáš R.', email: 'tomas@demo.com', avatar_url: null },
  { id: 'demo-emp-4', name: 'Jan N.', email: 'jan@demo.com', avatar_url: null },
  { id: 'demo-emp-5', name: 'Maria V.', email: 'maria@demo.com', avatar_url: null },
];

const employeeMap: Record<string, { id: string; name: string }> = {
  'Peter K.': { id: 'demo-emp-1', name: 'Peter K.' },
  'Lisa S.': { id: 'demo-emp-2', name: 'Lisa S.' },
  'Tomáš R.': { id: 'demo-emp-3', name: 'Tomáš R.' },
  'Jan N.': { id: 'demo-emp-4', name: 'Jan N.' },
  'Maria V.': { id: 'demo-emp-5', name: 'Maria V.' },
};

function makeShift(
  id: string,
  dayIndex: number,
  startHour: number,
  endHour: number,
  employeeName?: string,
  title?: string,
): DayShift {
  const emp = employeeName ? employeeMap[employeeName] : undefined;
  return {
    id,
    employeeName: emp?.name ?? '',
    employeeId: emp?.id,
    startTime: shiftTime(dayIndex, startHour),
    endTime: shiftTime(dayIndex, endHour),
    title: title || 'Shift',
    business_id: 'demo-business',
    calculatedStatus: emp ? 'scheduled' : 'unassigned',
  };
}

/** Initial demo shifts matching the existing demo layout */
export const INITIAL_DEMO_SHIFTS: DayShift[] = [
  // Monday (day 0)
  makeShift('e-1', 0, 8, 15, 'Peter K.', 'Morning'),
  makeShift('e-2', 0, 11, 19, 'Lisa S.', 'Mid-day'),
  makeShift('e-3', 0, 15, 20, 'Tomáš R.', 'Evening'),
  // Tuesday (day 1) — intentionally empty, AI will fill it
  // Wednesday (day 2)
  makeShift('e-4', 2, 8, 16, 'Jan N.', 'Morning'),
  makeShift('e-5', 2, 12, 20, 'Maria V.', 'Afternoon'),
  // Thursday (day 3)
  makeShift('e-6', 3, 8, 15, 'Peter K.', 'Morning'),
  makeShift('e-7', 3, 14, 20, 'Lisa S.', 'Evening'),
  // Friday (day 4)
  makeShift('e-8', 4, 9, 17, 'Tomáš R.', 'Morning'),
  makeShift('e-9', 4, 16, 20, undefined, 'Closing'),
  // Saturday (day 5)
  makeShift('e-10', 5, 10, 18, 'Jan N.', 'Weekend'),
];

/** The new shifts for "Create 2 shifts" option */
export const NEW_DEMO_SHIFTS: DayShift[] = [
  makeShift('new-1', 1, 8, 14, undefined, 'Morning'),
  makeShift('new-2', 1, 14, 20, undefined, 'Afternoon'),
];

/** The assigned versions of the 2-shift option */
export const ASSIGNED_DEMO_SHIFTS: DayShift[] = [
  makeShift('new-1', 1, 8, 14, 'Jan N.', 'Morning'),
  makeShift('new-2', 1, 14, 20, 'Tomáš R.', 'Afternoon'),
];

/** The new shifts for "Fill based on Monday" option (replicates Monday's 3-shift pattern) */
export const MONDAY_PATTERN_DEMO_SHIFTS: DayShift[] = [
  makeShift('new-1', 1, 8, 15, undefined, 'Morning'),
  makeShift('new-2', 1, 11, 19, undefined, 'Mid-day'),
  makeShift('new-3', 1, 15, 20, undefined, 'Evening'),
];

/** The assigned versions of the Monday-pattern shifts */
export const ASSIGNED_MONDAY_PATTERN_DEMO_SHIFTS: DayShift[] = [
  makeShift('new-1', 1, 8, 15, 'Jan N.', 'Morning'),
  makeShift('new-2', 1, 11, 19, 'Tomáš R.', 'Mid-day'),
  makeShift('new-3', 1, 15, 20, 'Maria V.', 'Evening'),
];

/** Demo opening hours for business hours overlay */
/** Demo employee roles */
export const DEMO_EMPLOYEE_ROLES: Record<string, string> = {
  'demo-emp-1': 'Waiter',
  'demo-emp-2': 'Barista',
  'demo-emp-3': 'Chef',
  'demo-emp-4': 'Waiter',
  'demo-emp-5': 'Manager',
};

/** Demo opening hours for business hours overlay */
export const DEMO_OPENING_HOURS = [
  { day_of_week: 1, is_open: true, opening_time: '08:00', closing_time: '20:00' },
  { day_of_week: 2, is_open: true, opening_time: '08:00', closing_time: '20:00' },
  { day_of_week: 3, is_open: true, opening_time: '08:00', closing_time: '20:00' },
  { day_of_week: 4, is_open: true, opening_time: '08:00', closing_time: '20:00' },
  { day_of_week: 5, is_open: true, opening_time: '08:00', closing_time: '20:00' },
  { day_of_week: 6, is_open: true, opening_time: '09:00', closing_time: '18:00' },
  { day_of_week: 0, is_open: true, opening_time: '10:00', closing_time: '17:00' },
];
